import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import styles from "../live-seo-page.module.css";

type MiamiInvisalignLocalization = {
	heroTitle: string;
	introHeading: string;
	practiceLink: string;
	introAfterPractice: string;
	treatmentLink: string;
	introAfterTreatment: string;
	invisalignLink: string;
	introAfterFirstInvisalign: string;
	bracesLink: string;
	introAfterBraces: string;
	introFinalInvisalign: string;
	introEnd: string;
	whatHeading: string;
	paragraphs: string[];
	contactLink: string;
	contactSuffix: string;
	homeLink: string;
	consultationSuffix: string;
	facebookLink: string;
	traySuffix: string;
	finalPrefix: string;
	freeConsultLink: string;
	finalSuffix: string;
};

const miamiInvisalignLocalization = {
	[Locale.EN]: {
		heroTitle: "Miami Invisalign",
		introHeading: "Miami Invisalign",
		practiceLink: "Miami Orthodontic Specialists",
		introAfterPractice:
			" are the premier Invisalign provider in the Miami area. Have you been thinking of orthodontic ",
		treatmentLink: "treatment",
		introAfterTreatment: "? ",
		invisalignLink: "Invisalign",
		introAfterFirstInvisalign:
			" is a great option for anyone looking for an alternative to ",
		bracesLink: "traditional braces",
		introAfterBraces: ". We are here to help you determine if ",
		introFinalInvisalign: "Invisalign",
		introEnd: " is right for you.",
		whatHeading: "What is Invisalign?",
		paragraphs: [
			"The Invisalign system is made of clear, lightweight plastic aligners that fit over your teeth and are designed to straighten your smile. They’re removable, making it easier for our patients in the Miami area to eat, drink, brush and floss.",
			"Their removability means you do not need to relearn how to brush and floss, nor do you need to change your diet. And since they are comfortable, you will often forget you are wearing the aligners. Our Invisalign patients at ",
			"Invisalign feels way more comfortable than traditional metal bracket braces and can give you the results you've always wanted.",
			"You’ll probably wear Invisalign aligners for 9–15 months and will typically use 18–30 sets during your treatment. You’ll even start to notice a change in just a few months!",
			"At your initial consultation at ",
			"You wear the trays for about 22 hours a day but can remove them to brush your teeth and eat. Based on your individual needs and the treatment plan put in place by ",
		],
		contactLink: "Miami Orthodontics Specialists",
		contactSuffix: " love the system, and you will, too.",
		homeLink: "Miami Orthodontic Specialists",
		consultationSuffix:
			", we'll take digital scans and X-rays to create a 3D image of your mouth. We make your first set of custom-fitted aligners from these images, and then you begin the process of straightening your teeth.",
		facebookLink: "Miami Orthodontic Specialists",
		traySuffix:
			", you’ll wear several sets of trays throughout your orthodontic treatment. Schedule a ",
		finalPrefix: "",
		freeConsultLink: "free consult",
		finalSuffix: " today.",
	},
	[Locale.ES]: {
		heroTitle: "Miami Invisalign",
		introHeading: "Miami Invisalign",
		practiceLink: "Miami Orthodontic Specialists",
		introAfterPractice:
			" es el principal proveedor de Invisalign en el área de Miami. ¿Has estado pensando en un ",
		treatmentLink: "tratamiento",
		introAfterTreatment: " de ortodoncia? ",
		invisalignLink: "Invisalign",
		introAfterFirstInvisalign:
			" es una excelente opción para quienes buscan una alternativa a los ",
		bracesLink: "brackets tradicionales",
		introAfterBraces: ". Estamos aquí para ayudarte a determinar si ",
		introFinalInvisalign: "Invisalign",
		introEnd: " es adecuado para ti.",
		whatHeading: "¿Qué es Invisalign?",
		paragraphs: [
			"El sistema Invisalign está hecho de alineadores transparentes y livianos de plástico que se ajustan sobre tus dientes y están diseñados para enderezar tu sonrisa. Son removibles, lo que facilita que nuestros pacientes en el área de Miami puedan comer, beber, cepillarse y usar hilo dental.",
			"Al ser removibles, no necesitas aprender de nuevo a cepillarte ni usar hilo dental, ni cambiar tu dieta. Y como son cómodos, a menudo olvidarás que llevas puestos los alineadores. A nuestros pacientes de Invisalign en ",
			"Invisalign se siente mucho más cómodo que los brackets metálicos tradicionales y puede darte los resultados que siempre has querido.",
			"Probablemente usarás los alineadores Invisalign durante 9 a 15 meses y normalmente usarás entre 18 y 30 juegos durante tu tratamiento. Incluso comenzarás a notar cambios en solo unos meses.",
			"En tu consulta inicial en ",
			"Usas las bandejas aproximadamente 22 horas al día, pero puedes quitártelas para cepillarte los dientes y comer. Según tus necesidades individuales y el plan de tratamiento establecido por ",
		],
		contactLink: "Miami Orthodontics Specialists",
		contactSuffix: " les encanta el sistema, y a ti también te encantará.",
		homeLink: "Miami Orthodontic Specialists",
		consultationSuffix:
			", tomaremos escaneos digitales y radiografías para crear una imagen 3D de tu boca. Con estas imágenes hacemos tu primer juego de alineadores personalizados, y luego comienzas el proceso de enderezar tus dientes.",
		facebookLink: "Miami Orthodontic Specialists",
		traySuffix:
			", usarás varios juegos de alineadores durante tu tratamiento ortodóncico. Programa una ",
		finalPrefix: "",
		freeConsultLink: "consulta gratuita",
		finalSuffix: " hoy.",
	},
} satisfies Record<Locale, MiamiInvisalignLocalization>;

export function MiamiInvisalignPage() {
	const getLocalization = useLocalization(miamiInvisalignLocalization);
	const paragraphs = getLocalization("paragraphs");

	return (
		<Flex direction="column">
			<HeroStyle2
				heroImageUrl="/miami-invisalign-hero.jpg"
				title={getLocalization("heroTitle")}
				backgroundPosition="center"
			/>
			<article className={styles.miamiInvisalignArticle}>
				<Heading as="h3" className={styles.miamiInvisalignHeading}>
					{getLocalization("introHeading")}
				</Heading>
				<Text as="p" className={styles.miamiInvisalignCopy}>
					<Link to="/" className={styles.inlineLink}>
						{getLocalization("practiceLink")}
					</Link>
					{getLocalization("introAfterPractice")}
					<Link to="/our-treatments" className={styles.inlineLink}>
						{getLocalization("treatmentLink")}
					</Link>
					{getLocalization("introAfterTreatment")}
					<Link to="/invisalign" className={styles.inlineLink}>
						{getLocalization("invisalignLink")}
					</Link>
					{getLocalization("introAfterFirstInvisalign")}
					<Link to="/life-with-braces" className={styles.inlineLink}>
						{getLocalization("bracesLink")}
					</Link>
					{getLocalization("introAfterBraces")}
					<Link to="/invisalign" className={styles.inlineLink}>
						{getLocalization("introFinalInvisalign")}
					</Link>
					{getLocalization("introEnd")}
				</Text>
				<OptimizedImage
					alt="Miami Invisalign"
					className={styles.miamiInvisalignImage}
					height={1335}
					src="/miami-invisalign-clear-braces.jpg"
					width={2000}
				/>
				<Heading as="h3" className={styles.miamiInvisalignHeading}>
					{getLocalization("whatHeading")}
				</Heading>
				<Text as="p" className={styles.miamiInvisalignCopy}>
					{paragraphs[0]}
				</Text>
				<Text as="p" className={styles.miamiInvisalignCopy}>
					{paragraphs[1]}
					<Link to="/contact-us" className={styles.inlineLink}>
						{getLocalization("contactLink")}
					</Link>
					{getLocalization("contactSuffix")}
				</Text>
				<Text as="p" className={styles.miamiInvisalignCopy}>
					{paragraphs[2]}
				</Text>
				<Text as="p" className={styles.miamiInvisalignCopy}>
					{paragraphs[3]}
				</Text>
				<Text as="p" className={styles.miamiInvisalignCopy}>
					{paragraphs[4]}
					<Link to="/" className={styles.inlineLink}>
						{getLocalization("homeLink")}
					</Link>
					{getLocalization("consultationSuffix")}
				</Text>
				<Text as="p" className={styles.miamiInvisalignCopy}>
					{paragraphs[5]}
					<a
						href="https://www.facebook.com/miamibraces"
						className={styles.inlineLink}
					>
						{getLocalization("facebookLink")}
					</a>
					{getLocalization("traySuffix")}
					{getLocalization("finalPrefix")}
					<Link to="/free-consult" className={styles.inlineLink}>
						{getLocalization("freeConsultLink")}
					</Link>
					{getLocalization("finalSuffix")}
				</Text>
			</article>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
